import Button from "components/Button";
import ImageSlider from "components/ImageSlider";
import RadioGroup from "components/RadioGroup";
import Stepper from "components/Stepper";
import Text from "components/Text";
import TextInput from "components/TextInput";
import { config } from "config";
import { FormEvent, memo, useState } from "react";
import { BiArrowBack } from "react-icons/bi";
import { useSignUpMutation } from "slice/userAuth";
import theme from "theme";
import { ReactComponent as Alimitless } from "../../images/alimitless.svg";
import { ReactComponent as Logo } from "../../images/logo.svg";
import * as Styled from "../shared";

export const Details = memo(() => {
    const [register] = useSignUpMutation(),
        [activeStep, setActiveStep] = useState(0),
        [inputs, setInputs] = useState<{
            firstName: string;
            lastName: string;
            gender: string;
            dob: string;
            type: "talent" | "agency";
        }>({
            firstName: "",
            lastName: "",
            gender: "",
            dob: "",
            type: "talent",
        });

    const onSubmit = async (event: FormEvent) => {
        event.preventDefault();

        try {
            const response = await register({ ...inputs });
        } catch (error) {}
    };

    return (
        <Styled.Wrapper>
            <Styled.LogoWrapper>
                <Logo />
                <Alimitless className="alimitless-logo" />
            </Styled.LogoWrapper>
            <Styled.LeftWrapper>
                <Styled.Card>
                    <Styled.StepperWrapper>
                        <Stepper
                            activeStep={activeStep}
                            onStepClick={setActiveStep}
                            fullWidth
                        >
                            <Stepper.Step />
                            <Stepper.Step />
                        </Stepper>
                        {activeStep === 0 && (
                            <Styled.StepTextWrapper>
                                <Text
                                    variant="heading5"
                                    align="center"
                                    fullWidth
                                >
                                    Basic Details
                                </Text>
                            </Styled.StepTextWrapper>
                        )}
                        {activeStep === 1 && (
                            <Styled.StepTextWrapper>
                                <Text
                                    variant="heading5"
                                    align="center"
                                    fullWidth
                                >
                                    Describe Yourself
                                </Text>
                            </Styled.StepTextWrapper>
                        )}
                    </Styled.StepperWrapper>
                    <Styled.Form onSubmit={onSubmit}>
                        {activeStep === 0 && (
                            <>
                                <TextInput
                                    type="text"
                                    label="First Name"
                                    required
                                    placeholder="John"
                                    fullWidth
                                    labelColor={theme.colors.text}
                                    value={inputs.firstName}
                                    onChange={(
                                        event: React.ChangeEvent<HTMLInputElement>
                                    ) =>
                                        setInputs((prevInputs) => ({
                                            ...prevInputs,
                                            firstName: event.target.value,
                                        }))
                                    }
                                />
                                <TextInput
                                    type="text"
                                    label="Last Name"
                                    required
                                    placeholder="Doe"
                                    fullWidth
                                    labelColor={theme.colors.text}
                                    value={inputs.lastName}
                                    onChange={(
                                        event: React.ChangeEvent<HTMLInputElement>
                                    ) =>
                                        setInputs((prevInputs) => ({
                                            ...prevInputs,
                                            lastName: event.target.value,
                                        }))
                                    }
                                />
                                <RadioGroup
                                    required
                                    fullWidth
                                    label="Gender"
                                    labelColor="#8A8A8A"
                                    selectedLabelColor={theme.colors.text}
                                    columns={3}
                                    value={inputs.gender}
                                    onChange={(
                                        event: React.ChangeEvent<HTMLInputElement>
                                    ) =>
                                        setInputs((prevInputs) => ({
                                            ...prevInputs,
                                            gender: event.target.value,
                                        }))
                                    }
                                    options={[
                                        {
                                            label: "Male",
                                            value: "male",
                                        },
                                        {
                                            label: "Female",
                                            value: "female",
                                        },
                                        {
                                            label: "Other",
                                            value: "other",
                                        },
                                    ]}
                                />

                                <Button
                                    type="button"
                                    onClick={() => setActiveStep(1)}
                                    variant="filled"
                                    fullWidth
                                    textColor={theme.colors.white}
                                    textVariant="button2"
                                >
                                    Next
                                </Button>
                            </>
                        )}
                        {activeStep === 1 && (
                            <>
                                <Styled.InfoWrapper>
                                    <Styled.InfoCard
                                        checked={inputs.type === "talent"}
                                        onClick={() =>
                                            setInputs((prevInputs) => ({
                                                ...prevInputs,
                                                type: "talent",
                                            }))
                                        }
                                    >
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <g clip-path="url(#clip0_784_4816)">
                                                <path d="M12 12C13.1867 12 14.3467 11.6481 15.3334 10.9888C16.3201 10.3295 17.0892 9.39246 17.5433 8.2961C17.9974 7.19975 18.1162 5.99335 17.8847 4.82946C17.6532 3.66558 17.0818 2.59648 16.2426 1.75736C15.4035 0.918247 14.3344 0.346802 13.1705 0.115291C12.0067 -0.11622 10.8003 0.00259972 9.7039 0.456726C8.60754 0.910851 7.67047 1.67989 7.01118 2.66658C6.35189 3.65328 6 4.81331 6 6C6.00159 7.59081 6.63424 9.11602 7.75911 10.2409C8.88399 11.3658 10.4092 11.9984 12 12ZM12 2C12.7911 2 13.5645 2.2346 14.2223 2.67412C14.8801 3.11365 15.3928 3.73836 15.6955 4.46927C15.9983 5.20017 16.0775 6.00444 15.9231 6.78036C15.7688 7.55629 15.3878 8.26902 14.8284 8.82843C14.269 9.38784 13.5563 9.7688 12.7804 9.92314C12.0044 10.0775 11.2002 9.99827 10.4693 9.69552C9.73836 9.39277 9.11365 8.88008 8.67412 8.22228C8.2346 7.56449 8 6.79113 8 6C8 4.93914 8.42143 3.92172 9.17157 3.17158C9.92172 2.42143 10.9391 2 12 2Z" />
                                                <path d="M12 14C9.61386 14.0026 7.32622 14.9517 5.63896 16.639C3.95171 18.3262 3.00265 20.6139 3 23C3 23.2652 3.10536 23.5196 3.29289 23.7071C3.48043 23.8946 3.73478 24 4 24C4.26522 24 4.51957 23.8946 4.70711 23.7071C4.89464 23.5196 5 23.2652 5 23C5 21.1435 5.7375 19.363 7.05025 18.0503C8.36301 16.7375 10.1435 16 12 16C13.8565 16 15.637 16.7375 16.9497 18.0503C18.2625 19.363 19 21.1435 19 23C19 23.2652 19.1054 23.5196 19.2929 23.7071C19.4804 23.8946 19.7348 24 20 24C20.2652 24 20.5196 23.8946 20.7071 23.7071C20.8946 23.5196 21 23.2652 21 23C20.9974 20.6139 20.0483 18.3262 18.361 16.639C16.6738 14.9517 14.3861 14.0026 12 14Z" />
                                            </g>
                                            <defs>
                                                <clipPath id="clip0_784_4816">
                                                    <rect
                                                        width="24"
                                                        height="24"
                                                        fill="white"
                                                    />
                                                </clipPath>
                                            </defs>
                                        </svg>
                                        <Text variant="label" color="#BDBDBD">
                                            I'm A
                                        </Text>
                                        <Text variant="button1">Talent</Text>
                                    </Styled.InfoCard>
                                    <Styled.InfoCard
                                        checked={inputs.type === "agency"}
                                        onClick={() =>
                                            setInputs((prevInputs) => ({
                                                ...prevInputs,
                                                type: "agency",
                                            }))
                                        }
                                    >
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <g clip-path="url(#clip0_784_4817)">
                                                <path
                                                    fill-rule="evenodd"
                                                    clip-rule="evenodd"
                                                    d="M11.3872 6.6986C11.3872 8.17841 10.1876 9.37803 8.70775 9.37803C7.22794 9.37803 6.02832 8.17841 6.02832 6.6986C6.02832 5.21879 7.22794 4.01917 8.70775 4.01917C10.1876 4.01917 11.3872 5.21879 11.3872 6.6986ZM8.70775 8.03831C9.44768 8.03831 10.0475 7.43851 10.0475 6.6986C10.0475 5.9587 9.44768 5.35888 8.70775 5.35888C7.96785 5.35888 7.36804 5.9587 7.36804 6.6986C7.36804 7.43851 7.96785 8.03831 8.70775 8.03831Z"
                                                />
                                                <path
                                                    fill-rule="evenodd"
                                                    clip-rule="evenodd"
                                                    d="M17.4163 8.70816C17.4163 10.8717 16.6274 12.851 15.3213 14.3739L16.6365 15.689C17.3339 15.3742 18.1829 15.5035 18.756 16.0766L23.445 20.7656C24.185 21.5055 24.185 22.7051 23.445 23.445C22.7051 24.185 21.5055 24.185 20.7656 23.445L16.0766 18.756C15.5035 18.1829 15.3743 17.3338 15.6891 16.6363L14.3739 15.3213C12.8511 16.6272 10.8718 17.4163 8.70816 17.4163C3.89878 17.4163 0 13.5176 0 8.70816C0 3.89878 3.89878 0 8.70816 0C13.5176 0 17.4163 3.89878 17.4163 8.70816ZM13.972 13.8643C15.274 12.5353 16.0766 10.7155 16.0766 8.70816C16.0766 4.63867 12.7777 1.33972 8.70816 1.33972C4.63867 1.33972 1.33972 4.63867 1.33972 8.70816C1.33972 10.7155 2.14239 12.5353 3.44428 13.8643C3.8428 12.0641 5.44838 10.7177 7.36844 10.7177H10.0479C11.968 10.7177 13.5735 12.0641 13.972 13.8643ZM12.7273 14.8849V14.7369C12.7273 13.257 11.5277 12.0574 10.0479 12.0574H7.36844C5.88863 12.0574 4.68901 13.257 4.68901 14.7369V14.8849C5.84489 15.6386 7.22537 16.0766 8.70816 16.0766C10.191 16.0766 11.5714 15.6386 12.7273 14.8849ZM17.8087 17.0239L22.4977 21.7129C22.7145 21.9297 22.7145 22.281 22.4977 22.4977C22.281 22.7145 21.9297 22.7145 21.7129 22.4977L17.0239 17.8087C16.8073 17.5919 16.8073 17.2405 17.0239 17.0239C17.2407 16.8071 17.5921 16.8071 17.8087 17.0239Z"
                                                />
                                            </g>
                                            <defs>
                                                <clipPath id="clip0_784_4817">
                                                    <rect
                                                        width="24"
                                                        height="24"
                                                        fill="white"
                                                    />
                                                </clipPath>
                                            </defs>
                                        </svg>
                                        <Text variant="label" color="#BDBDBD">
                                            I'm A
                                        </Text>
                                        <Text variant="button1">Agency</Text>
                                    </Styled.InfoCard>
                                </Styled.InfoWrapper>
                                <Styled.ButtonsWrapper>
                                    <Button
                                        type="button"
                                        onClick={() => setActiveStep(0)}
                                        variant="outlined"
                                        fullWidth
                                        textVariant="button2"
                                        icon={<BiArrowBack size={16} />}
                                        iconPosition="prefix"
                                    >
                                        Back
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="filled"
                                        fullWidth
                                        textColor={theme.colors.white}
                                        textVariant="button2"
                                    >
                                        Done
                                    </Button>
                                </Styled.ButtonsWrapper>
                            </>
                        )}
                    </Styled.Form>
                </Styled.Card>
            </Styled.LeftWrapper>
            <Styled.RightWrapper>
                <ImageSlider sliderImages={config.sliderImages} />
            </Styled.RightWrapper>
        </Styled.Wrapper>
    );
});
